import React, { useContext, useMemo } from "react";
import { HotelContext } from "../../context/HotelContext";
import { translate } from "../../utils/Translation";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Cell, Tooltip } from "recharts";

const WATER_DONATION_COLORS = ["#6C8EBF", "#1F3451"];
const DONATION_DISTRIBUTION_COLORS = ["#8FAED6", "#8FAED6", "#6C8EBF", "#2D4A75", "#1F3451"];

export const WaterDonationChart = ({ data, formatWater }) => {
  const defaultData = {
    total: 0,
    details: [
      { type: "GreenMiles", total: 0 },
      { type: "Lunishower", total: 0 },
    ],
  };

  const chartData = data || defaultData;
  const formatValue =
    formatWater ||
    ((value) => {
      if (value === null || value === undefined) return "--";
      if (value >= 1000) return `${(value / 1000).toFixed(0)}k`;
      return typeof value === "number" ? value.toString() : value;
    });

  const chartDataMemo = useMemo(() => {
    if (!chartData?.details || chartData.details.length === 0) {
      return { segments: [], allSegments: [], total: chartData?.total || 0 };
    }

    const total = chartData.total || 0;
    if (total === 0) {
      return {
        segments: [],
        allSegments: chartData.details.map((detail, index) => ({
          ...detail,
          color: WATER_DONATION_COLORS[index % WATER_DONATION_COLORS.length],
        })),
        total: 0,
      };
    }

    const segmentsWithValue = chartData.details.filter((detail) => detail.total > 0);
    const totalWithValue = segmentsWithValue.reduce((sum, detail) => sum + detail.total, 0);

    let currentAngle = -90;
    const segments = segmentsWithValue.map((detail, index) => {
      const percentage = (detail.total / totalWithValue) * 100;
      const angle = (percentage / 100) * 360;

      const segment = {
        ...detail,
        percentage,
        startAngle: currentAngle,
        endAngle: currentAngle + angle,
        color: WATER_DONATION_COLORS[index % WATER_DONATION_COLORS.length],
      };

      currentAngle += angle;
      return segment;
    });

    const allSegments = chartData.details.map((detail, index) => ({
      ...detail,
      color: WATER_DONATION_COLORS[index % WATER_DONATION_COLORS.length],
    }));

    return { segments, allSegments, total };
  }, [chartData]);

  const formatWaterValue = formatValue(chartDataMemo.total);
  const estimatedTextWidth = formatWaterValue.length * 9;
  const minRadiusForText = estimatedTextWidth / 2 + 15;
  const baseSize = 140;
  const thickness = 31;
  const availableRadius = (baseSize - thickness) / 2;
  const size = availableRadius < minRadiusForText ? minRadiusForText * 2 + thickness + 20 : baseSize;
  const radius = (size - thickness) / 2;
  const circumference = 2 * Math.PI * radius;
  const center = size / 2;

  const innerRadius = radius - thickness / 2;
  const maxTextWidth = innerRadius * 1.6;
  const baseFontSize = 18;
  const fontSize = estimatedTextWidth > maxTextWidth ? Math.max(14, baseFontSize * (maxTextWidth / estimatedTextWidth)) : baseFontSize;

  const segmentsWithStyle = useMemo(() => {
    if (chartDataMemo.segments.length === 0) return [];

    let cumulativeOffset = 0;
    return chartDataMemo.segments.map((segment) => {
      const percentage = segment.percentage / 100;
      const segmentLength = circumference * percentage;
      const gap = circumference - segmentLength;

      const style = {
        strokeDasharray: `${segmentLength} ${gap}`,
        strokeDashoffset: -cumulativeOffset,
      };

      cumulativeOffset += segmentLength;
      return { ...segment, style };
    });
  }, [chartDataMemo.segments, circumference]);

  if (chartDataMemo.segments.length === 0) {
    return (
      <div className="water-donation-chart">
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="monetary-chart__svg">
          <circle cx={center} cy={center} r={radius} fill="none" stroke="#E0E0E0" strokeWidth={thickness} />
          <text x={center} y={center} textAnchor="middle" dominantBaseline="middle" className="monetary-chart__total" fontSize={fontSize} fontWeight="600" fill="#365644">
            {formatValue(chartDataMemo.total)}
          </text>
        </svg>

        {chartDataMemo.allSegments && chartDataMemo.allSegments.length > 0 && (
          <div className="monetary-chart__legend">
            {chartDataMemo.allSegments.map((segment, index) => {
              const percentage = chartDataMemo.total > 0 ? Math.round((segment.total / chartDataMemo.total) * 100) : 0;
              return (
                <div key={index} className="monetary-chart__legend-item">
                  <div className="monetary-chart__legend-dot" style={{ backgroundColor: segment.color }} />
                  <span className="monetary-chart__legend-label">
                    {segment.type} {percentage}%
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="water-donation-chart">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="monetary-chart__svg">
        <circle cx={center} cy={center} r={radius} fill="none" stroke="#E0E0E0" strokeWidth={thickness} />

        {segmentsWithStyle.map((segment, index) => (
          <circle
            key={index}
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke={segment.color}
            strokeWidth={thickness}
            strokeDasharray={segment.style.strokeDasharray}
            strokeDashoffset={segment.style.strokeDashoffset}
            strokeLinecap="round"
            className="monetary-chart__segment"
            transform={`rotate(-90 ${center} ${center})`}
          />
        ))}

        <text x={center} y={center} textAnchor="middle" dominantBaseline="middle" className="monetary-chart__total" fontSize={fontSize} fontWeight="600" fill="#365644">
          {formatValue(chartDataMemo.total)}
        </text>
      </svg>

      {chartDataMemo.allSegments.length > 0 && (
        <div className="monetary-chart__legend">
          {chartDataMemo.allSegments.map((segment, index) => {
            const percentage = chartDataMemo.total > 0 ? Math.round((segment.total / chartDataMemo.total) * 100) : 0;
            return (
              <div key={index} className="monetary-chart__legend-item">
                <div className="monetary-chart__legend-dot" style={{ backgroundColor: segment.color }} />
                <span className="monetary-chart__legend-label">
                  {segment.type} {percentage}%
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export const DonationByStayDurationChart = ({ data }) => {
  const { lang } = useContext(HotelContext);

  const defaultData = [
    { duration: "1 jour", value: 0 },
    { duration: "2 jours", value: 0 },
    { duration: "3 jours", value: 0 },
    { duration: "4 jours ou +", value: 0 },
  ];

  const chartData = data || defaultData;

  const maxValue = Math.max(...chartData.map((item) => item.value), 15);
  const yAxisMax = Math.ceil(maxValue / 5) * 5;
  const yAxisTicks = Array.from({ length: Math.floor(yAxisMax / 5) + 1 }, (_, i) => i * 5);

  return (
    <div className="donation-by-duration-chart">
      <ResponsiveContainer width="100%" height={200}>
        <BarChart data={chartData} margin={{ top: 10, right: 20, left: 20, bottom: 30 }} barCategoryGap="15%">
          <CartesianGrid strokeDasharray="0" vertical={false} horizontal={true} stroke="rgba(27, 36, 27, 0.4)" strokeWidth={1} />
          <XAxis
            dataKey="duration"
            axisLine={true}
            tickLine={false}
            tick={{ fill: "#000000", fontSize: 16, fontFamily: "Montserrat", fontWeight: 400 }}
            interval={0}
            stroke="#000000"
            strokeWidth={2.3}
            height={30}
          />
          <YAxis
            axisLine={true}
            tickLine={false}
            domain={[0, yAxisMax]}
            ticks={yAxisTicks}
            tick={{ fill: "#000000", fontSize: 14, fontFamily: "Montserrat", fontWeight: 400 }}
            tickFormatter={(value) => value.toString()}
            stroke="#000000"
            strokeWidth={2.3}
            width={30}
          />
          <Tooltip
            cursor={{ fill: "rgba(0, 0, 0, 0.1)" }}
            contentStyle={{
              backgroundColor: "#fff",
              border: "1px solid #ccc",
              borderRadius: "4px",
              padding: "8px 12px",
            }}
            formatter={(value) => [value, translate("v2_rewards_donations_chart_count", lang)]}
            labelFormatter={(label) => translate("v2_donation_chart_duration", lang, { label })}
          />
          <Bar dataKey="value" fill="#8FAED6" radius={0} barSize={54} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export const DonationDistributionChart = ({ data }) => {
  const { lang } = useContext(HotelContext);
  const defaultData = [];
  const chartData = data || defaultData;

  const maxValue = Math.max(...chartData.map((item) => item.value), 15);
  const yAxisMax = Math.ceil(maxValue / 5) * 5;
  const yAxisTicks = Array.from({ length: Math.floor(yAxisMax / 5) + 1 }, (_, i) => i * 5);
  return (
    <div className="donation-distribution-chart">
      <ResponsiveContainer width="100%" height={200}>
        <BarChart data={chartData} margin={{ top: 10, right: 20, left: 20, bottom: 30 }} barCategoryGap="15%">
          <CartesianGrid strokeDasharray="0" vertical={false} horizontal={true} stroke="rgba(27, 36, 27, 0.4)" strokeWidth={1} />
          <XAxis
            dataKey="quantity"
            axisLine={true}
            tickLine={false}
            tick={{ fill: "#000000", fontSize: 16, fontFamily: "Montserrat", fontWeight: 400 }}
            interval={0}
            stroke="#000000"
            strokeWidth={2.3}
            height={30}
          />
          <YAxis
            axisLine={true}
            tickLine={false}
            domain={[0, yAxisMax]}
            ticks={yAxisTicks}
            tick={{ fill: "#000000", fontSize: 14, fontFamily: "Montserrat", fontWeight: 400 }}
            tickFormatter={(value) => value.toString()}
            stroke="#000000"
            strokeWidth={2.3}
            width={30}
          />
          <Tooltip
            cursor={{ fill: "rgba(0, 0, 0, 0.1)" }}
            contentStyle={{
              backgroundColor: "#fff",
              border: "1px solid #ccc",
              borderRadius: "4px",
              padding: "8px 12px",
            }}
            formatter={(value) => [value, translate("v2_rewards_donations_chart_count", lang)]}
            labelFormatter={(label) => translate("v2_donation_chart_quantity", lang, { label })}
          />
          <Bar dataKey="value" radius={0} barSize={54}>
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={DONATION_DISTRIBUTION_COLORS[index % DONATION_DISTRIBUTION_COLORS.length]} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};
